<template>
  <b-modal
    :visible="value"
    centered
    size="xl"
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    modal-class="dc-edit-modal"
    @change="$emit('input', $event)"
  >
    <div class="dc-edit-modal__wrapper">
      <!-- Custom header -->
      <div class="dc-edit-modal__header">
        <div class="dc-edit-modal__header-left">
          <div class="dc-edit-modal__avatar">
            <feather-icon
              icon="UsersIcon"
              size="24"
            />
          </div>
          <div class="dc-edit-modal__titles">
            <h4 class="dc-edit-modal__title">
              Edit Decision Committee
            </h4>
            <span class="dc-edit-modal__subtitle">
              {{ officeName || 'Select an office to manage its committee' }}
            </span>
          </div>
        </div>
        <div class="dc-edit-modal__header-right">
          <b-badge variant="light-primary">
            <feather-icon
              icon="UsersIcon"
              size="14"
              class="mr-25"
            />
            {{ members.length || 0 }} members
          </b-badge>
        </div>
      </div>

      <div class="dc-edit-modal__body">
        <!-- Existing Members Card -->
        <div class="dc-section-card">
          <div class="dc-section-card__header">
            <div class="dc-section-card__icon dc-section-card__icon--members">
              <feather-icon
                icon="UserCheckIcon"
                size="18"
              />
            </div>
            <div>
              <h5 class="dc-section-card__title">
                Existing Committee Members
              </h5>
              <small class="dc-section-card__subtitle">
                Toggle active status or remove members. Changes are applied only when you click <strong>Save</strong>.
              </small>
            </div>
          </div>

          <div class="dc-section-card__body">
            <b-table
              v-if="!editLoading"
              :items="members"
              :fields="fields"
              small
              bordered
              responsive
              hover
              head-variant="light"
              show-empty
              empty-text="No committee members added yet"
              class="dc-members-table"
            >
              <template #cell(name)="data">
                <div class="dc-members-table__name-cell">
                  <div class="dc-members-table__avatar">
                    {{ (data.item.name || '?').charAt(0).toUpperCase() }}
                  </div>
                  <div class="dc-members-table__name-texts">
                    <span class="dc-members-table__name-primary">
                      {{ data.item.name || '-' }}
                    </span>
                    <span class="dc-members-table__name-secondary">
                      {{ data.item.email || 'No email' }}
                    </span>
                  </div>
                </div>
              </template>

              <template #cell(phone)="data">
                <span class="dc-members-table__phone">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="mr-25 text-muted"
                  />
                  {{ data.item.phone || '-' }}
                </span>
              </template>

              <template #cell(role_name)="data">
                <span class="dc-members-table__role-badge">
                  <feather-icon
                    icon="AwardIcon"
                    size="14"
                    class="mr-25"
                  />
                  {{ data.item.role_name || '-' }}
                </span>
              </template>

              <template #cell(active)="data">
                <b-form-checkbox
                  :checked="!!data.item.active"
                  switch
                  @change="$emit('toggle-active', { index: data.index, value: $event })"
                >
                  <span
                    :class="[
                      'dc-members-table__active-label',
                      data.item.active ? 'dc-members-table__active-label--on' : 'dc-members-table__active-label--off',
                    ]"
                  >
                    <feather-icon
                      :icon="data.item.active ? 'CheckCircleIcon' : 'SlashIcon'"
                      size="14"
                      class="mr-25"
                    />
                    {{ data.item.active ? 'Active' : 'Inactive' }}
                  </span>
                </b-form-checkbox>
              </template>

              <template #cell(actions)="data">
                <b-btn
                  variant="outline-danger"
                  size="sm"
                  class="dc-members-table__remove-btn"
                  @click="$emit('remove-member', data.index)"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    size="14"
                    class="mr-25"
                  />
                  Remove
                </b-btn>
              </template>
            </b-table>

            <div
              v-else
              class="text-center my-2"
            >
              <b-spinner
                type="grow"
                label="Loading..."
              />
            </div>
          </div>
        </div>

        <!-- Add New Member Card -->
        <div class="dc-section-card dc-section-card--secondary">
          <div class="dc-section-card__header">
            <div class="dc-section-card__icon dc-section-card__icon--add">
              <feather-icon
                icon="UserPlusIcon"
                size="18"
              />
            </div>
            <div>
              <h5 class="dc-section-card__title">
                Add New Committee Member
              </h5>
              <small class="dc-section-card__subtitle">
                Fill the fields and click <strong>Add</strong>. You can queue multiple members before saving.
              </small>
            </div>
          </div>

          <div class="dc-section-card__body">
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <label class="d-block font-weight-bold">
                  <feather-icon
                    icon="AwardIcon"
                    size="14"
                    class="mr-25 text-primary"
                  />
                  Role<span class="text-danger">*</span>
                </label>
                <VSelect
                  :value="newMember.role"
                  :options="roleOptions"
                  placeholder="Select role"
                  :reduce="option => option.value"
                  label="label"
                  @input="updateNewMember({ role: $event })"
                />
              </b-col>

              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <label class="d-block font-weight-bold">
                  <feather-icon
                    icon="UserIcon"
                    size="14"
                    class="mr-25 text-primary"
                  />
                  Name<span class="text-danger">*</span>
                </label>
                <b-form-input
                  :value="newMember.name"
                  placeholder="Member name"
                  @input="updateNewMember({ name: $event })"
                />
              </b-col>

              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <label class="d-block font-weight-bold">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="mr-25 text-primary"
                  />
                  Email<span class="text-danger">*</span>
                </label>
                <b-form-input
                  :value="newMember.email"
                  type="email"
                  placeholder="Member email"
                  @input="updateNewMember({ email: $event })"
                />
              </b-col>

              <b-col
                cols="12"
                md="6"
                class="mb-1"
              >
                <label class="d-block font-weight-bold">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="mr-25 text-primary"
                  />
                  Phone<span class="text-danger">*</span>
                </label>
                <b-form-input
                  :value="newMember.phone"
                  type="tel"
                  placeholder="Member phone"
                  @input="updateNewMember({ phone: $event })"
                />
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between align-items-center mt-1">
              <small class="text-muted dc-add-hint">
                All fields are required. Email must be valid. New members are <strong>Active</strong> by default.
              </small>

              <b-btn
                variant="primary"
                size="sm"
                class="dc-add-btn"
                @click="$emit('add-member')"
              >
                <feather-icon
                  icon="PlusCircleIcon"
                  size="14"
                  class="mr-25"
                />
                Add Member
              </b-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal footer actions -->
      <div class="dc-edit-modal__footer">
        <b-btn
          variant="outline-secondary"
          class="mr-1"
          :disabled="saveLoading"
          @click="$emit('input', false)"
        >
          <feather-icon
            icon="XCircleIcon"
            size="14"
            class="mr-25"
          />
          Cancel
        </b-btn>

        <b-button
          v-if="!saveLoading"
          variant="primary"
          class="dc-save-btn"
          @click="$emit('save')"
        >
          <feather-icon
            icon="SaveIcon"
            size="14"
            class="mr-25"
          />
          Save Changes
        </b-button>

        <b-button
          v-else
          variant="primary"
          disabled
          class="d-flex align-items-center dc-save-btn"
        >
          <b-spinner
            v-for="item in 3"
            :key="item"
            small
            type="grow"
          />
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import VSelect from 'vue-select'

export default {
  name: 'DecisionCommitteeEditModal',
  components: { VSelect },
  props: {
    value: {
      type: Boolean,
      default: () => false,
    },
    officeName: {
      type: String,
      default: () => '',
    },
    editLoading: {
      type: Boolean,
      default: () => false,
    },
    saveLoading: {
      type: Boolean,
      default: () => false,
    },
    members: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    roleOptions: {
      type: Array,
      default: () => [],
    },
    newMember: {
      type: Object,
      default: () => ({
        role: null,
        name: '',
        email: '',
        phone: '',
        active: true, // default active; can be changed later via table toggle
      }),
    },
  },
  methods: {
    updateNewMember(patch) {
      this.$emit('update:newMember', { ...this.newMember, ...patch })
    },
  },
}
</script>

<style scoped lang="scss">
@import './DecisionCommitteeEditModal.scss';
</style>
